/**
 * Document Hooks
 *
 * Hooks for fetching documents and getting access URLs.
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { documentsApi } from "@/api/services";
import type { ApiError } from "@/api/client";

/**
 * Fetch organization documents
 */
export function useDocuments(enabled = false) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["documents", "list"],
    queryFn: () => documentsApi.listDocuments(),
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });

  return {
    data: data ?? [],
    loading: isLoading,
    error,
    refetch,
  };
}

/**
 * Get presigned access URL and open document in new tab
 * Handles 404 and 500 with toasts; 403 handled by API client/token provider
 */
export function useDocumentAccess() {
  const mutation = useMutation({
    mutationFn: (documentId: string) =>
      documentsApi.getDocumentAccess(documentId),
    onSuccess: (data) => {
      window.open(data.url, "_blank", "noopener,noreferrer");
    },
    onError: (error: ApiError) => {
      const status = error?.status;
      if (status === 404) {
        toast.error("Document not found");
      } else if (status === 500) {
        toast.error("Failed to load document. Please try again.");
      } else {
        toast.error(error?.message ?? "Failed to load document");
      }
    },
  });

  return {
    execute: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
